import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Mail, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import Footer from "../components/Footer";

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1569598525846-cc400d20fac4?w=1920&q=80"
            alt="Store Interior"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-background/80" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-unbounded text-4xl md:text-5xl font-bold text-foreground mb-6">
              About <span className="gradient-text">Airo Vapor</span>
            </h1>
            <p className="font-outfit text-lg text-muted-foreground max-w-2xl mx-auto">
              Your premier destination for quality vaporizers, e-liquids, and
              accessories in Gardner, Kansas. We're passionate about providing
              the best products and customer experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Store Info Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map / Location */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                {/* Google Maps Embed */}
                <div className="aspect-video bg-muted">
                  <iframe
                    title="Airo Vapor Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3109.8!2d-94.9272!3d38.8108!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDQ4JzM5LjAiTiA5NMKwNTUnMzguMCJX!5e0!3m2!1sen!2sus!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="grayscale"
                  />
                </div>
                <div className="p-6">
                  <Button
                    data-testid="open-maps-btn"
                    asChild
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <a
                      href="https://maps.app.goo.gl/7jYJikWFQLMz9qhK9"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open in Google Maps
                    </a>
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Store Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col justify-center"
            >
              <h2 className="font-unbounded text-2xl md:text-3xl font-bold text-foreground mb-6">
                Visit Our Store
              </h2>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-foreground mb-1">
                      Address
                    </h3>
                    <p className="font-outfit text-muted-foreground">
                      1823 E Santa Fe St
                      <br />
                      Gardner, KS 66030
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-foreground mb-1">
                      Phone
                    </h3>
                    <a
                      href="tel:+19136051001"
                      className="font-outfit text-muted-foreground hover:text-primary transition-colors"
                    >
                      (913) 605-1001
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-foreground mb-1">
                      Store Hours
                    </h3>
                    <p className="font-outfit text-muted-foreground">
                      Monday - Sunday
                      <br />
                      10:00 AM - 8:00 PM
                    </p>
                  </div>
                </div>
              </div>

              <Separator className="my-8 bg-border" />

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="call-now-btn"
                  asChild
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <a href="tel:+19136051001">
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </a>
                </Button>
                <Button
                  data-testid="get-directions-about-btn"
                  asChild
                  variant="outline"
                  className="border-border hover:bg-muted"
                >
                  <a
                    href="https://maps.app.goo.gl/7jYJikWFQLMz9qhK9"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Promise Section */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-unbounded text-2xl md:text-3xl font-bold text-foreground mb-6">
              Our Promise
            </h2>
            <p className="font-outfit text-lg text-muted-foreground mb-8">
              At Airo Vapor, we're committed to providing only the highest
              quality products from trusted brands. Our knowledgeable staff is
              always ready to help you find exactly what you're looking for,
              whether you're a seasoned enthusiast or just getting started.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl">✓</span>
                </div>
                <h3 className="font-unbounded text-lg font-semibold text-foreground mb-2">
                  Premium Products
                </h3>
                <p className="font-outfit text-sm text-muted-foreground">
                  Carefully curated selection of top-tier brands
                </p>
              </div>
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <span className="text-2xl">💬</span>
                </div>
                <h3 className="font-unbounded text-lg font-semibold text-foreground mb-2">
                  Expert Guidance
                </h3>
                <p className="font-outfit text-sm text-muted-foreground">
                  Friendly staff with deep product knowledge
                </p>
              </div>
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-accent/10 flex items-center justify-center">
                  <span className="text-2xl">🏪</span>
                </div>
                <h3 className="font-unbounded text-lg font-semibold text-foreground mb-2">
                  Local Convenience
                </h3>
                <p className="font-outfit text-sm text-muted-foreground">
                  Order online, pick up in-store same day
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
